import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, DatePicker, Form, Input, Modal, Row, Switch, Typography } from "antd";
import Card from "antd/lib/card/Card";
import { isValidPhoneNumber } from "libphonenumber-js";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { graphql, useFragment, useRefetchableFragment } from "react-relay";

import * as CLOUD_API from "@skydio/api_util/src/backends/cloud_api";
import enums_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/enums_pb";
import { Alert, Divider, toast, Tooltip } from "@skydio/rivet-ui";

import { useConnectionNodes } from "components/common/Pagination";
import { useUpsertUser } from "hooks/mutation/upsertUser";
import { useSelector } from "hooks/redux";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useIsAdmin } from "hooks/useIsAdmin";
import { getCurrentUserEmail, getOrgName, getOrgUuid } from "state/current_user/selectors";
import { getOnlyChangedValues } from "utils/common";
import { displayMutationResults } from "utils/mutations";
import {
  getReadableOrgRoleFromGqlPermission,
  getReadableOrgRoleFromValue,
  getReadableOrgRoles,
  getResourceGroupDisplayName,
} from "utils/team";
import { trackUserAction } from "utils/trackEvent";

import AxonConnectOverrideSelection from "./AxonConnectOverrideSelection";
import { UserProfileFooter } from "./footers";
import OneTimePasswordEnabledSelection from "./OneTimePasswordEnabledSelection";
import { orgLoginMethodsFragment } from "./OrgLoginMethods";
import { OrgPermissionHelpLink } from "./OrgPermissionHelpLink";
import RoleGroupFormSection from "./RoleGroupFormSection";
import SsoProviderLoginTypeSelection, { idpFragment } from "./SsoProviderLoginTypeSelection";
import { useOrganizationResourceGroups } from "./useOrganizationResourceGroups";

import styles from "./UserProfile.module.scss";

import type { Gutter } from "antd/lib/grid/row";
import type { Moment } from "moment";
import type { FC } from "react";
import type { ValueType } from "@skydio/core";
import type { OrgLoginMethods_loginMethods$key } from "__generated__/OrgLoginMethods_loginMethods.graphql";
import type { SsoProviderLoginTypeSelection_idpData$key } from "__generated__/SsoProviderLoginTypeSelection_idpData.graphql";
import type { identityprovidertype, login_type } from "__generated__/TeamView_organization.graphql";
import type {
  LoginTypesUpdate,
  organizationpermission,
  ResourceGroupLinkInput,
} from "__generated__/upsertUserMutation.graphql";
import type { UserProfile_loginTypes$key } from "__generated__/UserProfile_loginTypes.graphql";
import type { RoleWithResourceGroups } from "./RoleGroupFormSection";

export interface IdentityProviderConfiguration {
  id: string;
  uuid: string;
  name: string;
  providerType: identityprovidertype;
}

export interface LoginType {
  loginType: login_type;
  identityProviderConfiguration: IdentityProviderConfiguration | null;
  authEnabled: boolean;
}

interface Props {
  onCancel: () => void;
  onRemove: () => void;
  idpDataRef: SsoProviderLoginTypeSelection_idpData$key;
  userRef: UserProfile_loginTypes$key;
  statsRef: OrgLoginMethods_loginMethods$key;
}

interface FormUser {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  organizationNotes: string;
  expiry?: Moment;
}

interface EditUserFormFields {
  user: FormUser;
  roleWithResourceGroups: RoleWithResourceGroups[];
  loginTypeOverride: boolean | null;
  loginOverrides: LoginTypesUpdate[] | null;
}

const loginTypeFragment = graphql`
  fragment UserProfile_loginTypes on User {
    id
    uuid
    email
    firstName
    lastName
    organizationPermission
    loginTypesOverriden
    phoneNumber
    expiry
    organizationNotes
    resourceGroupLinks {
      edges {
        node {
          resourceGroupId
          resourceGroupName
          resourceGroupPermission
        }
      }
    }
    loginTypes {
      __id
      edges {
        node {
          loginType
          authEnabled
          identityProviderConfiguration {
            id
            name
            providerType
            uuid
          }
        }
      }
    }
  }
`;

const UserProfileModalTitle = ({ titleText }: { titleText: string }) => {
  return <div className="font-semibold">{titleText}</div>;
};

const UserProfile: FC<Props> = ({ onCancel, idpDataRef, userRef, statsRef }) => {
  const { t } = useTranslation("user");
  const [form] = Form.useForm();
  const [formSubmitDisabled, setFormSubmitDisabled] = useState(false);
  const [formHasSkydioUser, setFormHasSkydioUser] = useState(false);

  const orgUuid = useSelector(getOrgUuid);
  const orgName = useSelector(getOrgName);

  const { resourceGroups } = useOrganizationResourceGroups({ orgUuid });

  const [editedSomething, setEditedSomething] = useState(false);

  const missionsPageEnabled = useFeatureFlag("missionsPage");
  const vehicleTeleopEnabled = useFeatureFlag("vehicleTeleoperation");
  const showRemotePilotRole = missionsPageEnabled || vehicleTeleopEnabled;
  const supportUserExpiryEnabled = useFeatureFlag("supportUserExpiry");
  const enableDistributionList = useFeatureFlag("enableDistributionList");
  const readableOrgRoles = getReadableOrgRoles(showRemotePilotRole);

  const fragmentUser = useFragment(loginTypeFragment, userRef);
  const [, refetchStats] = useRefetchableFragment(orgLoginMethodsFragment, statsRef);

  const currentUserEmail = useSelector(getCurrentUserEmail);
  const isAdmin = useIsAdmin();
  const email = fragmentUser?.email || "";
  const id = fragmentUser?.id || "";
  const loginTypeOverride = fragmentUser?.loginTypesOverriden || false;
  const loginTypes = useConnectionNodes(fragmentUser?.loginTypes);

  const idpData = useFragment(idpFragment, idpDataRef);
  const idps = useConnectionNodes(idpData.identityProviderConfigurations);
  const axonIdp = idps.find(item => item.providerType === "AXON") ?? null;
  const orgDefaultLoginTypes = useConnectionNodes(idpData.loginTypes);
  const defaultSsoLoginTypegql =
    orgDefaultLoginTypes.find(item => item.loginType === "SSO") ?? null;
  const defaultAxonLoginTypegql =
    orgDefaultLoginTypes.find(item => item.loginType === "AXON") ?? null;

  const currentSsoLoginType = loginTypes.find(({ loginType }) => loginType === "SSO");
  const currentAxonLoginType = useMemo(
    () => loginTypes.find(({ loginType }) => loginType === "AXON"),
    [loginTypes]
  );
  const [ssoLoginIdp, setSsoLoginIdp] = useState("disabled");
  const [axonEnabled, setAxonEnabled] = useState(currentAxonLoginType?.authEnabled ?? false);

  const [userHasSkydioEmail, setUserHasSkydioEmail] = useState(email.endsWith("@skydio.com"));
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const loginOverrides = useMemo(() => {
    return loginTypes.map(
      ({ authEnabled, identityProviderConfiguration, loginType }: LoginType): LoginTypesUpdate => {
        return {
          authEnabled: authEnabled,
          identityProviderConfigurationId: identityProviderConfiguration?.uuid,
          loginType: loginType,
        };
      }
    );
  }, [loginTypes]);

  const onUserProfileValuesChange = useCallback(() => {
    form.validateFields([["user", "expiry"]]);
    form.validateFields([["user", "email"]]);
    if (enableDistributionList) {
      form.validateFields([["user", "phoneNumber"]]);
    }
    const email = form.getFieldValue(["user", "email"]);
    setUserHasSkydioEmail(email.endsWith("@skydio.com"));
    setErrorMessages([]);
  }, [form, enableDistributionList]);

  const resourceGroupLinksList = useConnectionNodes(fragmentUser?.resourceGroupLinks);

  // (granular permissions) Initial form values based on user's existing permissions
  const initialFormValues: EditUserFormFields = useMemo(() => {
    const { organizationPermission } = fragmentUser;
    const roleWithGroupsMap = new Map();

    // Convert resourceGroupLinks to roleWithResourceGroups (Remote Pilot + Moderator roles)
    resourceGroupLinksList.forEach(rglink => {
      const readableRole = getReadableOrgRoleFromGqlPermission(rglink.resourceGroupPermission);
      const groupName = resourceGroups.find(rg => rg.uuid === rglink.resourceGroupId)?.name;
      if (groupName) {
        if (!roleWithGroupsMap.has(readableRole)) {
          roleWithGroupsMap.set(readableRole, [groupName]);
        } else {
          roleWithGroupsMap.get(readableRole).push(groupName);
        }
      }
    });

    // Add empty groups array to for Organization Admin and Pilot roles
    if (organizationPermission === "ADMIN") {
      roleWithGroupsMap.set(getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.ADMIN), []);
    }
    if (organizationPermission === "SKYDIO_SUPERUSER") {
      setFormHasSkydioUser(true);
      roleWithGroupsMap.set(
        getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER),
        []
      );
    }
    if (organizationPermission === "SKYDIO_READONLY") {
      setFormHasSkydioUser(true);
      roleWithGroupsMap.set(
        getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY),
        []
      );
    }

    // only include pilot row if user has no other resource group level permissions (i.e hide the Pilot role row when user has any access to the cloud)
    if (organizationPermission === "MEMBER" && resourceGroupLinksList.length === 0) {
      roleWithGroupsMap.set(
        getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MEMBER),
        []
      );
    }

    const roleWithResourceGroups: RoleWithResourceGroups[] = Array.from(roleWithGroupsMap).map(
      ([rgRole, resourceGroupNames]) => ({
        rgRole,
        resourceGroupNames,
      })
    );

    return {
      user: {
        email,
        firstName: fragmentUser?.firstName || "",
        lastName: fragmentUser?.lastName || "",
        phoneNumber: fragmentUser?.phoneNumber || "",
        organizationNotes: fragmentUser?.organizationNotes || "",
        expiry: fragmentUser?.expiry ? moment(fragmentUser?.expiry) : undefined,
      },
      roleWithResourceGroups,
      loginTypeOverride,
      loginOverrides,
    };
  }, [
    fragmentUser,
    resourceGroupLinksList,
    email,
    loginTypeOverride,
    loginOverrides,
    resourceGroups,
  ]);

  const [upsertUser] = useUpsertUser(
    displayMutationResults(response => response.upsertUser, {
      onSuccess: () => {
        refetchStats({}, { fetchPolicy: "network-only" });
        onCancel();
      },
      successMessage: m => `User profile updated${m.user?.email ? ` for ${m.user?.email}` : ""}`,
    })
  );

  const handleSave = useCallback(
    (update: EditUserFormFields) => {
      const recordUpdate: Record<string, unknown> = {
        user: update.user,
        roleWithResourceGroups: update.roleWithResourceGroups,
      };
      const recordInitFormValues: Record<string, unknown> = {
        user: initialFormValues.user,
        roleWithResourceGroups: initialFormValues.roleWithResourceGroups,
      };
      const editedFields = getOnlyChangedValues(recordUpdate, recordInitFormValues);

      if (_.size(editedFields)) {
        const {
          email = "",
          firstName = "",
          lastName = "",
          phoneNumber = "",
          organizationNotes = "",
          expiry,
        } = update.user;
        const roleWithResourceGroups = update.roleWithResourceGroups;
        const setLoginTypeOverride = update.loginTypeOverride;
        const loginOverrides = update.loginOverrides;

        const errorMsgs = [];

        // Ensure that each group is only mapped to one role (only for Cloud User and Remote Pilot roles)
        const groupToRoleMap = new Map<string, string[]>();
        const groupsWithMultipleRoles = new Set<string>(); // Use a Set to avoid duplicates

        roleWithResourceGroups.forEach(({ rgRole, resourceGroupNames }) => {
          if (
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.ADMIN) ||
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MEMBER) ||
            rgRole ===
              getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER) ||
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY)
          )
            return;
          resourceGroupNames.forEach(rgName => {
            if (groupToRoleMap.has(rgName)) {
              const existingRoles = groupToRoleMap.get(rgName);
              if (existingRoles && !existingRoles.includes(rgRole)) {
                groupToRoleMap.set(rgName, existingRoles.concat(rgRole));
                groupsWithMultipleRoles.add(rgName);
              }
            } else {
              groupToRoleMap.set(rgName, [rgRole]);
            }
          });
        });

        // convert to display name for error message if needed
        const groupsWithMultipleRolesArray = Array.from(groupsWithMultipleRoles).map(group =>
          getResourceGroupDisplayName(orgName, group)
        );

        if (groupsWithMultipleRolesArray.length > 0) {
          errorMsgs.push(
            `Only one role may be assigned to ${groupsWithMultipleRolesArray.join(", ")}`
          );
        }

        // Ensure that Pilot and Admin are not both selected at the same time
        const hasAdmin = roleWithResourceGroups.some(
          ({ rgRole }) =>
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.ADMIN)
        );
        const hasPilot = roleWithResourceGroups.some(
          ({ rgRole }) =>
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MEMBER)
        );
        const hasSuperuser = roleWithResourceGroups.some(
          ({ rgRole }) =>
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER)
        );
        const hasReadOnly = roleWithResourceGroups.some(
          ({ rgRole }) =>
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY)
        );
        if (
          [hasAdmin, hasPilot, hasSuperuser, hasReadOnly].reduce(
            (acc, val) => acc + (val ? 1 : 0),
            0
          ) > 1
        ) {
          errorMsgs.push(
            "Organization Admin, Pilot, and Skydio roles cannot be assigned at the same time"
          );
        }

        // Halt submission if there are any errors
        if (errorMsgs.length > 0) {
          setErrorMessages(errorMsgs);
          return;
        }

        let orgLevelPermission = CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
          CLOUD_API.modules.organizations.utils.orgPermissionEnum.MEMBER
        ); // Default user level org permission is MEMBER
        if (hasAdmin) {
          orgLevelPermission = CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
            CLOUD_API.modules.organizations.utils.orgPermissionEnum.ADMIN
          );
        } else if (hasSuperuser) {
          orgLevelPermission = CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
            CLOUD_API.modules.organizations.utils.orgPermissionEnum.SKYDIO_SUPERUSER
          );
        } else if (hasReadOnly) {
          orgLevelPermission = CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
            CLOUD_API.modules.organizations.utils.orgPermissionEnum.SKYDIO_READONLY
          );
        } else if (hasPilot) {
          orgLevelPermission = CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
            CLOUD_API.modules.organizations.utils.orgPermissionEnum.MEMBER
          );
        }

        // Convert form inputs to resourceGroupLinks for upsertUser mutation
        const resourceGroupLinks: ResourceGroupLinkInput[] = [];
        roleWithResourceGroups.forEach(({ rgRole, resourceGroupNames }) => {
          if (
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.ADMIN) ||
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MEMBER) ||
            rgRole ===
              getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER) ||
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY)
          ) {
            return;
          }
          resourceGroupNames.forEach(rgName => {
            const resourceGroupId = resourceGroups.find(({ name }) => name === rgName)?.id || "";
            const resourceGroupLevelPermission =
              rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.REMOTE_PILOT)
                ? CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
                    CLOUD_API.modules.organizations.utils.orgPermissionEnum.REMOTE_PILOT
                  )
                : CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
                    CLOUD_API.modules.organizations.utils.orgPermissionEnum.MODERATOR
                  );

            if (resourceGroupId) {
              resourceGroupLinks.push({
                resourceGroupNodeId: resourceGroupId,
                resourceGroupPermission: resourceGroupLevelPermission as organizationpermission,
              });
            }
          });
        });

        // Use zero epoch time to explicity clear expiry if the form field is unset
        const parsedExpiry = expiry ? expiry.utc().toISOString() : moment.utc(0).toISOString();

        setEditedSomething(true);
        upsertUser(
          email,
          id,
          firstName,
          lastName,
          enableDistributionList ? phoneNumber : null,
          orgLevelPermission as organizationpermission,
          organizationNotes,
          resourceGroupLinks,
          setLoginTypeOverride,
          loginOverrides,
          parsedExpiry
        );

        trackUserAction("edit_user_profile", { user_email: email, fields: _.keys(editedFields) });
      } else {
        toast.info(t("messages.noEditedFields", { email }));
      }
    },
    [
      initialFormValues.user,
      initialFormValues.roleWithResourceGroups,
      upsertUser,
      id,
      email,
      orgName,
      resourceGroups,
      enableDistributionList,
      t,
    ]
  );

  const handleFormChanged = useCallback(() => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
    setFormSubmitDisabled(hasErrors);
    if (
      form
        .getFieldValue("roleWithResourceGroups")
        .some(
          ({ rgRole }: RoleWithResourceGroups) =>
            rgRole ===
              getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER) ||
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY)
        )
    ) {
      setFormHasSkydioUser(true);
    } else {
      setFormHasSkydioUser(false);
    }
  }, [form]);

  const handleClose = useCallback(() => {
    onCancel();

    if (!editedSomething) {
      trackUserAction("close_user_profile_without_editing", { user_email: email });
    }
  }, [email, onCancel, editedSomething]);

  const validateNameLength = useCallback(async () => {
    const userValues = form.getFieldValue("user");
    const firstName = userValues?.firstName;
    const lastName = userValues?.lastName;

    // A space is added, limiting first+last to 254
    if ((firstName?.length || 0) + (lastName?.length || 0) > 254) {
      throw new Error(t("messages.namesTooLongError"));
    }
  }, [form, t]);

  const validateEmail = useCallback(async () => {
    const userValues = form.getFieldValue("user");
    const email = userValues?.email;

    if (!email.match(/\S+@\S+\.\S+/)) {
      throw new Error("Not a valid email address.");
    }

    if (
      formHasSkydioUser &&
      !email.endsWith("@skydio.com") // Do frontend check to prevent SKYDIO_SUPERUSER and SKYDIO_READONLY roles from being assigned to non-Skydio users
    ) {
      throw new Error("Only Skydio users can have Skydio roles.");
    }
  }, [form, formHasSkydioUser]);

  const validatePhoneNumber = useCallback(async () => {
    if (!enableDistributionList) {
      return;
    }

    const userValues = form.getFieldValue("user");
    const phoneNumber = userValues?.phoneNumber;

    if (!phoneNumber) {
      return; // Empty phone number is valid (optional field)
    }

    if (!isValidPhoneNumber(phoneNumber)) {
      throw new Error("Invalid phone number.");
    }
  }, [form, enableDistributionList]);

  const convertReadableOrgRole = useCallback(
    (value: string): ValueType<enums_pb.OrgPermission.PermEnumMap> => {
      const orgPermission = readableOrgRoles.find(
        ({ readableName }) => readableName.toUpperCase() === value.toUpperCase()
      );
      if (orgPermission) {
        return orgPermission.enumValue as ValueType<enums_pb.OrgPermission.PermEnumMap>;
      } else {
        return enums_pb.OrgPermission.PermEnum.MODERATOR;
      }
    },
    [readableOrgRoles]
  );

  const validateOrgRole = useCallback(
    async (_: unknown, role: string) => {
      if (!role) {
        throw new Error("Missing Org Role");
      } else if (!convertReadableOrgRole(role)) {
        throw new Error(`Invalid role : ${role}`);
      }
    },
    [convertReadableOrgRole]
  );

  const expiryValidators = useMemo(
    () => [
      {
        validator: (_: unknown, expiry?: Moment) => {
          if (expiry && expiry.isBefore(moment())) {
            return Promise.reject("Expiry date must be in the future.");
          }
          return Promise.resolve();
        },
      },
      {
        validator: async (_: unknown, expiry?: Moment) => {
          const skydioUserRole: RoleWithResourceGroups | undefined = form
            .getFieldValue("roleWithResourceGroups")
            .find(
              ({ rgRole }: RoleWithResourceGroups) =>
                rgRole ===
                  getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY) ||
                rgRole ===
                  getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER)
            );
          if (!expiry && skydioUserRole !== undefined) {
            return Promise.reject(
              <span className="text-gray-500">
                Skydio recommends setting an expiry for {skydioUserRole.rgRole} users.
              </span>
            );
          }
          return Promise.resolve();
        },
        warningOnly: true,
      },
    ],
    [form]
  );

  const nameLengthValidators = useMemo(
    () => [
      {
        validator: validateNameLength,
      },
    ],
    [validateNameLength]
  );

  const emailValidators = useMemo(
    () => [
      {
        validator: validateEmail,
      },
    ],
    [validateEmail]
  );

  const phoneNumberValidators = useMemo(
    () => [
      {
        validator: validatePhoneNumber,
      },
    ],
    [validatePhoneNumber]
  );

  const otpOnChange = useCallback(
    (value: boolean) => {
      form.setFieldsValue({ loginOverrides: [{ authEnabled: value }] });
    },
    [form]
  );

  const setOverrideOnChange = useCallback(
    (enabled: boolean) => {
      form.setFieldsValue({ overrideLoginTypes: enabled });
      setSsoLoginIdp(defaultSsoLoginTypegql?.identityProviderConfiguration?.uuid ?? "disabled");
      setAxonEnabled(defaultAxonLoginTypegql?.authEnabled ?? false);
    },
    [
      defaultAxonLoginTypegql?.authEnabled,
      defaultSsoLoginTypegql?.identityProviderConfiguration?.uuid,
      form,
    ]
  );

  // Form paths
  const firstNamePath = useMemo(() => ["user", "firstName"], []);
  const lastNamePath = useMemo(() => ["user", "lastName"], []);
  const emailPath = useMemo(() => ["user", "email"], []);
  const phoneNumberPath = useMemo(() => ["user", "phoneNumber"], []);
  const organizationNotesPath = useMemo(() => ["user", "organizationNotes"], []);
  const expiryPath = useMemo(() => ["user", "expiry"], []);

  useEffect(() => {
    setSsoLoginIdp(currentSsoLoginType?.identityProviderConfiguration?.uuid ?? "disabled");
  }, [currentSsoLoginType]);

  useEffect(() => {
    setAxonEnabled(currentAxonLoginType?.authEnabled ?? false);
  }, [currentAxonLoginType]);

  useEffect(() => {
    form.resetFields();
    setEditedSomething(false);
    setErrorMessages([]); // clear error messages
  }, [email, form]);

  const gutterArray = useMemo(() => [8 as Gutter, 0 as Gutter] as [Gutter, Gutter], []);

  const overrideLoginTypesTitle = useMemo(
    () => <span className="font-semibold">Override Login Types</span>,
    []
  );

  const modalTitleText = useMemo(() => {
    if (email === currentUserEmail) {
      return t("selfTitle");
    } else {
      return t("otherTitle");
    }
  }, [email, currentUserEmail, t]);

  const firstNameDeps = useMemo(() => [firstNamePath], [firstNamePath]);
  const lastNameDeps = useMemo(() => [lastNamePath], [lastNamePath]);

  const phoneNumberLabel = useMemo(
    () => (
      <div>
        {t("fields.phoneNumber")}{" "}
        <Tooltip content="Include country code in phone number (e.g. +1)">
          <FontAwesomeIcon icon={faInfoCircle} />
        </Tooltip>
      </div>
    ),
    [t]
  );

  return (
    <Modal
      visible={email !== ""}
      title={UserProfileModalTitle({ titleText: modalTitleText })}
      footer={UserProfileFooter({
        disabledSubmit: formSubmitDisabled,
        submitText: t("buttons.save"),
        onClickSubmit: form.submit,
        onClickCancel: handleClose,
      })}
      className={styles.container}
      onCancel={handleClose}
      transitionName=""
    >
      {/* Display error messages after form submission if present */}
      {errorMessages.length > 0 && (
        <div className="mb-4">
          {errorMessages.map((errorMessage, index) => (
            <Alert key={index} status="error">
              <Alert.Content>
                <Alert.Title>{errorMessage}</Alert.Title>
              </Alert.Content>
            </Alert>
          ))}
        </div>
      )}
      <Form
        form={form}
        colon={false}
        layout="vertical"
        initialValues={initialFormValues}
        onValuesChange={onUserProfileValuesChange}
        onFinish={handleSave}
        onFieldsChange={handleFormChanged}
      >
        <Row gutter={24}>
          <Col flex="auto">
            <Row gutter={gutterArray}>
              <Col span={12}>
                <Form.Item
                  name={firstNamePath}
                  label={t("fields.firstName")}
                  dependencies={lastNameDeps}
                  rules={nameLengthValidators}
                >
                  <Input size="large" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={lastNamePath}
                  label={t("fields.lastName")}
                  dependencies={firstNameDeps}
                  rules={nameLengthValidators}
                >
                  <Input size="large" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={gutterArray}>
              <Col span={12}>
                <Form.Item
                  name={emailPath}
                  label={t("fields.email")}
                  dependencies={emailPath}
                  rules={emailValidators}
                >
                  <Input size="large"></Input>
                </Form.Item>
              </Col>
              <Col span={12}>
                {enableDistributionList && (
                  <Form.Item
                    name={phoneNumberPath}
                    label={phoneNumberLabel}
                    dependencies={phoneNumberPath}
                    rules={phoneNumberValidators}
                  >
                    <Input size="large"></Input>
                  </Form.Item>
                )}
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item name={organizationNotesPath} label={t("fields.note")}>
                  <Input.TextArea />
                </Form.Item>
              </Col>
            </Row>
            {supportUserExpiryEnabled && (
              <>
                <Row>
                  <Col span={24}>
                    <Form.Item
                      name={expiryPath}
                      label={t("fields.expiry")}
                      rules={expiryValidators}
                    >
                      <DatePicker showTime allowClear format="YYYY-MM-DD HH:mm:ss" />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </Col>
        </Row>
        {isAdmin && (
          <>
            <Divider className="mb-4" />
            <div className="mb-2">
              <div className="pr-1 font-semibold text-legacyGray-900">Login Types</div>
              <div className="text-legacyGray-400">
                All users can log in with their email in addition to one single sign-on (SSO)
                method.
              </div>
            </div>
            <Form.List name="loginOverrides">
              {/* Skip adding Form.Items here to get the styling correct */}
              {fields => (
                <div className="max-w-lg space-y-3">
                  {fields.map(field => (
                    <div className="flex w-full">
                      <Card className="w-full" key={field.key}>
                        <div className="flex w-full items-center justify-between">
                          {form.getFieldValue(["loginOverrides", field.name, "loginType"]) ===
                            "ONE_TIME_PASSWORD" && (
                            <div className="flex w-full items-center justify-between">
                              <Typography.Text className="font-semibold">Email</Typography.Text>
                              <OneTimePasswordEnabledSelection
                                initialState={form.getFieldValue([
                                  "loginOverrides",
                                  field.name,
                                  "authEnabled",
                                ])}
                                onChange={otpOnChange}
                              />
                            </div>
                          )}
                          {form.getFieldValue(["loginOverrides", field.name, "loginType"]) ===
                            "SSO" && (
                            <div className="flex w-full items-center justify-between">
                              <Typography.Text className="font-semibold">SSO</Typography.Text>
                              <SsoProviderLoginTypeSelection
                                // TODO(david.taylor): figure out pattern for callback inside map of form list
                                // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                                onChange={value => {
                                  if (value === "disabled") {
                                    form.setFieldValue(
                                      [
                                        "loginOverrides",
                                        field.name,
                                        "identityProviderConfigurationId",
                                      ],
                                      null
                                    );
                                    form.setFieldValue(
                                      ["loginOverrides", field.name, "authEnabled"],
                                      false
                                    );
                                  } else {
                                    form.setFieldValue(
                                      [
                                        "loginOverrides",
                                        field.name,
                                        "identityProviderConfigurationId",
                                      ],
                                      value
                                    );
                                    form.setFieldValue(
                                      ["loginOverrides", field.name, "authEnabled"],
                                      true
                                    );
                                  }
                                  setSsoLoginIdp(value);
                                }}
                                currentSelection={ssoLoginIdp}
                                disabled={form.getFieldValue("loginTypeOverride") === false}
                                idpDataRef={idpDataRef}
                              />
                            </div>
                          )}
                          {form.getFieldValue(["loginOverrides", field.name, "loginType"]) ===
                            "AXON" && (
                            <div className="flex w-full items-center justify-between">
                              <Typography.Text className="font-semibold">
                                Axon Connect
                              </Typography.Text>
                              <AxonConnectOverrideSelection
                                // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                                onChange={value => {
                                  if (value) {
                                    form.setFieldValue(
                                      ["loginOverrides", field.name, "authEnabled"],
                                      true
                                    );
                                    form.setFieldValue(
                                      [
                                        "loginOverrides",
                                        field.name,
                                        "identityProviderConfigurationId",
                                      ],
                                      axonIdp?.uuid ?? false
                                    );
                                  } else {
                                    form.setFieldValue(
                                      ["loginOverrides", field.name, "authEnabled"],
                                      false
                                    );
                                    form.setFieldValue(
                                      [
                                        "loginOverrides",
                                        field.name,
                                        "identityProviderConfigurationId",
                                      ],
                                      null
                                    );
                                  }
                                  setAxonEnabled(value);
                                }}
                                currentSelection={axonEnabled}
                                disabled={form.getFieldValue("loginTypeOverride") === false}
                              />
                            </div>
                          )}
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              )}
            </Form.List>

            <div className="mt-4">
              <Form.Item name="loginTypeOverride" label={overrideLoginTypesTitle}>
                <Switch
                  checked={form.getFieldValue("overrideLoginTypes")}
                  onChange={setOverrideOnChange}
                />
              </Form.Item>
            </div>
          </>
        )}
        <>
          <Divider className="mb-4" />
          <RoleGroupFormSection
            form={form}
            orgName={orgName}
            resourceGroups={resourceGroups}
            convertReadableOrgRole={convertReadableOrgRole}
            validateOrgRole={validateOrgRole}
            showSkydioRoles={userHasSkydioEmail}
          />
          {supportUserExpiryEnabled && formHasSkydioUser && (
            <Alert status="warning">
              <Alert.Content>
                <Alert.Title>This user will have Skydio internal permissions</Alert.Title>
                <Alert.Description>
                  <OrgPermissionHelpLink />
                </Alert.Description>
              </Alert.Content>
            </Alert>
          )}
        </>
      </Form>
    </Modal>
  );
};

export default UserProfile;
